import { Component, OnInit, ViewChild } from '@angular/core';
import { AdminService } from '../admin.service';
import { NotificationMessageService } from '../../shared/notification-message/notification-message.service';
import { DeviceManagementService } from 'src/app/device/device-management.service';
import { UsersService } from 'src/app/users/users.service';
import { Router } from '@angular/router';
import { ColumnsConfig, TableConfig, TableData} from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { TranslateService } from '@ngx-translate/core';
import { ModalComponent } from 'src/app/shared/modal/modal.component';

@Component({
	selector: 'app-upload-fw-files',
	templateUrl: './upload-fw-files.component.html',
	styleUrls: ['./upload-fw-files.component.css'],
})

export class UploadFwFilesComponent implements OnInit {
	@ViewChild('editNoteModal') editNoteModal!: ModalComponent;

	// table data
	tableData: TableData[] = [];
	tableConfig: TableConfig = {
		hasExport: false,
		hasPagination: false,
	};

	columnConfig: ColumnsConfig[] = [
		{ key: 'fw_files_list', name: this.translate.instant('devices.fw_files_list'), type: "string", width: '10rem'},
		{ key: 'note', name: this.translate.instant('devices.note'), type: "string", width: '36rem'},
		{ key: 'edit', name: this.translate.instant('g.edit'), type: "icon", width: '5rem'},
		{ key: 'action', name: this.translate.instant('g.action'), type: "icon", width: '5rem'},
	];

	minFilesCount: number = 2;

	files: File[] = [];
	maxAllowedFileSize: number = 15000000;
	fileNames: string = '';

	successExtension: boolean;
	successSize: boolean;
	successFilesCount: boolean;

	fwList: String[] = [];
	fwVersion: string = '';

	editingVersion: string | null = null;
	editingNote: string = '';
	noteMaxLength: number = 2000;

	constructor (
		private router: Router,
		private userService: UsersService,
		private adminService: AdminService,
		private notificationMessage: NotificationMessageService,
		private deviceManagementService: DeviceManagementService,
		public translate: TranslateService
	) { }

	ngOnInit() {
		if(!this.userService.hasAccessFunction('upload_fw_files'))
			this.router.navigate(['unauthorized']);

		this.getTestingFWFilesList();
	}

	ngAfterViewInit() {
		this.editNoteModal.onClose.subscribe((ok) => {
			if (!ok)
				return;
			this.saveNote();
		});
	}

	checkFile(event: any) {
		const uploadedFiles = event.target.files;
		this.files = [];
		this.fileNames = '';
		this.fwVersion = '';
		this.successExtension = true;
		this.successSize = true;
		this.successFilesCount = true;

		if (!uploadedFiles || uploadedFiles.length !== this.minFilesCount) {
			this.successFilesCount = false;
			return;
		}

		let sizeM = '';
		let versionStr = '';
		let has1k = false;
		let has4k = false;

		const regex = /^TruckView_enc_(16M|4M)_(4kChunks|1kChunks)_V(\d{1,2}\.\d{2,4})\.bin$/;

		for (let i = 0; i < uploadedFiles.length; i++) {
			const file = uploadedFiles[i];
			const match = file.name.match(regex);

			if (!match) {
				this.notificationMessage.setMessage('translate|error_msgs.invalid_file_name_format');
				return;
			}

			const currentSizeM = match[1];
			const chunkType = match[2].toLowerCase();
			const currentVersionStr = match[3];

			if (i === 0) {
				sizeM = currentSizeM;
				versionStr = currentVersionStr;
			} else {
				if (currentSizeM !== sizeM || currentVersionStr !== versionStr) {
					this.notificationMessage.setMessage('translate|error_msgs.files_must_match_version_and_size');
					return;
				}
			}

			if (chunkType === '1kchunks') has1k = true;
			if (chunkType === '4kchunks') has4k = true;

			if (!file.type.startsWith('application') && !file.name.endsWith('.bin')) {
				this.successExtension = false;
				return;
			}

			if (file.size > this.maxAllowedFileSize || file.size <= 0) {
				this.successSize = false;
				return;
			}
		}

		if (!has1k || !has4k) {
			this.notificationMessage.setMessage('translate|error_msgs.must_include_1k_and_4k_files');
			return;
		}

		this.successExtension = true;
		this.successSize = true;
		this.fwVersion = versionStr;
		this.files = Array.from(uploadedFiles);
		this.fileNames = this.files.map(f => f.name).join(', ');
	}

	getTestingFWFilesList() {
		this.deviceManagementService.getTestingFWFilesList().subscribe((res: any) => {
			const tableData = [];
			const files = res?.fwFiles || (res?.fwVersions || []).map((v: string) => ({ version: v, note: '' }));
			files.sort((a: { version: string }, b: { version: string }) => parseFloat(a.version) - parseFloat(b.version));

			for(const fw of files) {
				tableData.push({
					fw_files_list: {value: fw.version},
					note: {value: fw.note || '', forceWrap: true},
					edit: {icon: 'edit', iconColor: '#007bff', action: () => this.openEditNoteModal(fw.version)},
					action: {icon: 'trash', iconColor: 'red', action: () => this.deleteFWFile(fw.version)}
				});
			}

			this.tableData = tableData;
		});
	}

	openEditNoteModal(version: string) {
		const row = this.tableData.find(r => r['fw_files_list']?.value === version);
		this.editingVersion = version;
		this.editingNote = row?.['note']?.value || '';
		this.editNoteModal.show();
	}

	saveNote() {
		if (!this.editingVersion)
			return;

		this.adminService.updateFWNote(this.editingVersion, this.editingNote).subscribe(() => {
			const row = this.tableData.find(r => r['fw_files_list']?.value === this.editingVersion);
			if (row)
				row['note'] = { value: this.editingNote, forceWrap: true };

			this.editingVersion = null;
			this.editingNote = '';
			this.notificationMessage.setMessage('globalSuccessMsg');
		});
	}

	upload() {
		if (this.files.length !== this.minFilesCount)
			return;

		const formData = new FormData();

		for (const file of this.files) {
			formData.append('files', file);
		}

		this.adminService.uploadFWFile(formData).subscribe(res => {
			if (res == 1) {
				const fileName = (+this.fwVersion).toFixed(4);

				this.tableData.push({
					fw_files_list: {value: fileName},
					note: {value: '', forceWrap: true},
					edit: {icon: 'edit', iconColor: '#007bff', action: () => this.openEditNoteModal(fileName)},
					action: {icon: 'trash', iconColor: 'red', action: () => this.deleteFWFile(fileName)}
				});

				this.notificationMessage.setMessage('globalSuccessMsg');
				return this.getTestingFWFilesList();
			}

			return this.notificationMessage.setMessage(`translate|error_msgs.${res}`);
		})
	}

	deleteFWFile(file: string) {
		this.deviceManagementService.deleteFWFile(file).subscribe((res: any) => {
			this.getTestingFWFilesList();
			return this.notificationMessage.setMessage('globalSuccessMsg');
		});
	}
}
